import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Pencil, ShieldCheck, X } from 'lucide-react';
import { Field, PersonAvatar } from '@/components/catalog/CatalogParts';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useRevokeOnlineAccess, useSetOnlineAccess } from '@/features/onlineAccounts/hooks';
import {
  SHARE_LEVELS,
  SHARE_LEVEL_DESCRIPTIONS,
  SHARE_LEVEL_LABELS,
  describeAccess,
  friendlyOnlineAccountError,
  hasLiveAccess,
  localIsoDate,
  loginHolderName,
  loginLabel,
  profileName,
  type OnlineAccountRow,
} from '@/features/onlineAccounts/model';
import type { SeatProfile } from '@/features/software/model';
import { formatDate, todayIsoDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import type { OnlineAccountShareAccess } from '@/types/database';

type Scope = 'account' | 'logins';

export function OnlineAccountShareDialog({
  open,
  row,
  shareable,
  superAdmins,
  profilesById,
  onOpenChange,
}: {
  open: boolean;
  row: OnlineAccountRow;
  shareable: SeatProfile[];
  superAdmins: SeatProfile[];
  profilesById: Map<string, SeatProfile>;
  onOpenChange: (open: boolean) => void;
}) {
  const setAccess = useSetOnlineAccess();
  const revoke = useRevokeOnlineAccess();
  const isOwner = row.myAccess === 'owner';
  const [personId, setPersonId] = useState('');
  const [level, setLevel] = useState<OnlineAccountShareAccess>('view');
  const [scope, setScope] = useState<Scope>('account');
  const [loginIds, setLoginIds] = useState<string[]>([]);
  const [expiresOn, setExpiresOn] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);

  const reset = () => {
    setPersonId('');
    setLevel('view');
    setScope('account');
    setLoginIds([]);
    setExpiresOn('');
  };

  useEffect(() => {
    if (!open) return;
    reset();
    setConfirmRemove(null);
  }, [open, row.id]);

  const existing = useMemo(
    () => new Map(row.people.map((p) => [p.profileId, p])),
    [row.people],
  );
  const current = personId ? existing.get(personId) : undefined;
  const lockedForMe = !isOwner && current?.access === 'manage';

  const loadPerson = (id: string) => {
    setPersonId(id);
    const found = existing.get(id);
    if (!found) {
      setLevel('view');
      setScope('account');
      setLoginIds([]);
      setExpiresOn('');
      return;
    }
    setLevel(found.access);
    setScope(found.loginIds.length ? 'logins' : 'account');
    setLoginIds(found.loginIds);
    setExpiresOn(found.expiresAt && !found.expired ? localIsoDate(found.expiresAt) : '');
  };

  const people = useMemo(() => {
    const list = [...shareable];
    for (const p of row.people) {
      if (!p.inactive && !list.some((s) => s.id === p.profileId)) {
        const profile = profilesById.get(p.profileId);
        if (profile) list.push(profile);
      }
    }
    return list.sort((a, b) => (a.full_name || a.email).localeCompare(b.full_name || b.email));
  }, [shareable, row.people, profilesById]);

  const levels = scope === 'logins' ? SHARE_LEVELS.filter((l) => l !== 'manage') : SHARE_LEVELS;
  const ready =
    !!personId && !lockedForMe && (scope === 'account' || loginIds.length > 0) &&
    (level !== 'manage' || isOwner);
  const pending = setAccess.isPending || revoke.isPending;

  const submit = () => {
    void setAccess
      .mutateAsync({
        accountId: row.id,
        profileId: personId,
        access: level,
        loginIds: scope === 'logins' ? loginIds : [],
        expiresOn,
      })
      .then(() => {
        const name = profileName(personId, profilesById) ?? 'They';
        toast.success(current ? `${name}’s access updated.` : `Shared with ${name}.`);
        reset();
      })
      .catch((err) => toast.error(friendlyOnlineAccountError(err, 'Could not share the account.')));
  };

  const remove = (profileId: string) => {
    void revoke
      .mutateAsync({ accountId: row.id, profileId })
      .then(() => {
        toast.success(`${profileName(profileId, profilesById) ?? 'Their'} access removed.`);
        setConfirmRemove(null);
        if (personId === profileId) reset();
      })
      .catch((err) => toast.error(friendlyOnlineAccountError(err, 'Could not remove access.')));
  };

  const sortedPeople = [...row.people].sort((a, b) =>
    (profileName(a.profileId, profilesById) ?? '').localeCompare(
      profileName(b.profileId, profilesById) ?? '',
    ),
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Share {row.name}</DialogTitle>
          <DialogDescription>
            Only the people listed here can see this account. Everyone else, including new
            employees, can’t until you add them.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-4 rounded-lg border border-border p-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (ready) submit();
          }}
        >
          <Field label="Person">
            <Select value={personId} onValueChange={loadPerson}>
              <SelectTrigger>
                <SelectValue placeholder="Pick someone to share with" />
              </SelectTrigger>
              <SelectContent>
                {people.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.full_name || p.email}
                    {existing.has(p.id) ? ' · has access' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          {lockedForMe ? (
            <p className="text-sm text-muted-foreground">
              Only a super admin can change full access.
            </p>
          ) : (
            personId && (
              <>
                <div className="space-y-1.5">
                  <span className="text-sm font-medium text-foreground">What they can see</span>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <ScopeButton
                      active={scope === 'account'}
                      title="The whole account"
                      detail="Every login, now and later"
                      onClick={() => {
                        setScope('account');
                        setLoginIds([]);
                      }}
                    />
                    <ScopeButton
                      active={scope === 'logins'}
                      title="Only some logins"
                      detail={row.logins.length ? 'Pick which ones' : 'Add a login first'}
                      disabled={row.logins.length === 0}
                      onClick={() => {
                        setScope('logins');
                        if (level === 'manage') setLevel('view');
                      }}
                    />
                  </div>
                </div>

                {scope === 'logins' && (
                  <div className="space-y-1 rounded-md border border-border p-2">
                    {row.logins.map((login) => {
                      const checked = loginIds.includes(login.id);
                      const holder = loginHolderName(login, profilesById);
                      return (
                        <label
                          key={login.id}
                          className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md px-2 hover:bg-secondary"
                        >
                          <Checkbox
                            checked={checked}
                            ariaLabel={loginLabel(login)}
                            onCheckedChange={(next) =>
                              setLoginIds((prev) =>
                                next ? [...prev, login.id] : prev.filter((id) => id !== login.id),
                              )
                            }
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm text-foreground">
                              {loginLabel(login)}
                            </span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {login.role === 'admin' ? 'Admin' : 'User'}
                              {holder ? ` · ${holder}` : ''}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}

                <Field label="Access">
                  <Select
                    value={level}
                    onValueChange={(value) => setLevel(value as OnlineAccountShareAccess)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {levels.map((option) => (
                        <SelectItem
                          key={option}
                          value={option}
                          disabled={option === 'manage' && !isOwner}
                        >
                          {scope === 'logins' && option === 'edit'
                            ? 'Can change password'
                            : SHARE_LEVEL_LABELS[option]}
                          {option === 'manage' && !isOwner ? ' (super admins only)' : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    {scope === 'logins'
                      ? level === 'edit'
                        ? 'View, copy, and change the password on those logins.'
                        : 'View and copy the password on those logins.'
                      : SHARE_LEVEL_DESCRIPTIONS[level]}
                  </p>
                </Field>

                <Field
                  label="Access ends"
                  htmlFor="share-expires"
                  hint={expiresOn ? 'Access stops at the end of that day.' : 'Leave blank to keep access until you remove it.'}
                >
                  <div className="flex gap-2">
                    <Input
                      id="share-expires"
                      type="date"
                      min={todayIsoDate()}
                      value={expiresOn}
                      onChange={(e) => setExpiresOn(e.target.value)}
                    />
                    {expiresOn && (
                      <Button type="button" variant="outline" onClick={() => setExpiresOn('')}>
                        Clear
                      </Button>
                    )}
                  </div>
                </Field>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" onClick={reset}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={pending || !ready}>
                    {current ? 'Update access' : 'Share'}
                  </Button>
                </div>
              </>
            )
          )}
        </form>

        <div className="space-y-2">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Who has access
          </h3>
          <ul className="divide-y divide-border rounded-lg border border-border">
            <li className="flex items-center gap-3 px-3 py-2.5">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-info-soft text-info">
                <ShieldCheck className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-sm text-foreground">Super admins</span>
                <span className="block truncate text-xs text-muted-foreground">
                  {superAdmins.map((p) => p.full_name || p.email).join(', ') || 'Always'} · every
                  account
                </span>
              </span>
            </li>
            {sortedPeople.length === 0 && (
              <li className="px-3 py-4 text-center text-sm text-muted-foreground">
                Not shared with anyone else yet.
              </li>
            )}
            {sortedPeople.map((person) => {
              const name = profileName(person.profileId, profilesById) ?? 'Former member';
              const canChange = isOwner || person.access !== 'manage';
              return (
                <li key={person.profileId} className="flex items-center gap-3 px-3 py-2">
                  <PersonAvatar
                    id={person.profileId}
                    name={name}
                    profile={profilesById.get(person.profileId)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-2">
                      <span className="truncate text-sm text-foreground">{name}</span>
                      {person.inactive ? (
                        <Badge variant="muted">Deactivated</Badge>
                      ) : (
                        person.expired && <Badge variant="muted">Expired</Badge>
                      )}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {person.inactive
                        ? 'No access while their Atlas account is off'
                        : describeAccess(person, row)}
                      {hasLiveAccess(person) && person.expiresAt
                        ? ` · until ${formatDate(person.expiresAt)}`
                        : ''}
                    </span>
                  </span>
                  {canChange &&
                    (confirmRemove === person.profileId ? (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={pending}
                        onClick={() => remove(person.profileId)}
                      >
                        Remove
                      </Button>
                    ) : (
                      <span className="flex shrink-0">
                        {!person.inactive && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-11 w-11"
                            aria-label={`Change ${name}’s access`}
                            onClick={() => loadPerson(person.profileId)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-11 w-11"
                          aria-label={`Remove ${name}’s access`}
                          onClick={() => setConfirmRemove(person.profileId)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </span>
                    ))}
                </li>
              );
            })}
          </ul>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScopeButton({
  active,
  title,
  detail,
  disabled,
  onClick,
}: {
  active: boolean;
  title: string;
  detail: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={cn(
        'rounded-lg border px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        active
          ? 'border-info bg-info-soft/40 text-foreground'
          : 'border-border text-foreground hover:bg-secondary',
      )}
    >
      <span className="block text-sm font-medium">{title}</span>
      <span className="block text-xs text-muted-foreground">{detail}</span>
    </button>
  );
}
